import { API_ENDPOINTS } from './apiConfig';
import { Language, SUPPORTED_LANGUAGES } from '../models/Language';

class LanguageService {
  /**
   * Fetches all available languages from the API
   * @returns {Promise<Language[]>} Array of available languages
   */
  async fetchAvailableLanguages() {
    console.log('[LanguageService] Fetching available languages from API');
    
    try {
      const response = await fetch(API_ENDPOINTS.fetchAppLanguage, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('[LanguageService] API response status:', response.status);

      if (response.ok && response.status === 200) {
        const languagesData = await response.json();
        console.log('[LanguageService] API response data:', languagesData);
        
        // Parse the response and convert to Language objects
        const languages = languagesData.map(json => Language.fromJson(json));
        console.log(`[LanguageService] Successfully fetched ${languages.length} languages`);
        
        return languages;
      } else {
        console.warn('[LanguageService] API returned non-200 status, using fallback languages');
        return SUPPORTED_LANGUAGES;
      }
    } catch (error) {
      console.error('[LanguageService] Error fetching languages:', error);
      console.log('[LanguageService] Using fallback supported languages');
      return SUPPORTED_LANGUAGES;
    }
  }

  /**
   * Fetches multilingual text for given keys
   * @param {string[]} keys - Array of text keys to fetch
   * @param {string} languageCode - Language code (e.g., 'en', 'hi')
   * @returns {Promise<Object>} Object with key-value pairs of translated text
   */
  async fetchMultilingualText(keys, languageCode = 'en') {
    console.log('[LanguageService] Fetching multilingual text for keys:', keys, 'language:', languageCode);
    
    try {
      // Build query parameters for GET request
      const url = new URL(API_ENDPOINTS.fetchMultilingualText);
      url.searchParams.append('keys', keys.join(','));
      url.searchParams.append('languageCode', languageCode);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const textData = await response.json();
        console.log('[LanguageService] Successfully fetched multilingual text');
        return textData;
      } else {
        console.warn('[LanguageService] Failed to fetch multilingual text');
        return {};
      }
    } catch (error) {
      console.error('[LanguageService] Error fetching multilingual text:', error);
      return {};
    }
  }

  /**
   * Converts input text to multilingual format
   * @param {string} inputText - Text to convert
   * @param {string} targetLanguageCode - Target language code
   * @returns {Promise<string>} Translated text
   */
  async convertInputToMultilingual(inputText, targetLanguageCode = 'en') {
    console.log('[LanguageService] Converting text to multilingual:', inputText, 'target:', targetLanguageCode);
    
    try {
      // Build query parameters for GET request
      const url = new URL(API_ENDPOINTS.convertInputToMultilingual);
      url.searchParams.append('inputText', inputText);
      url.searchParams.append('targetLanguageCode', targetLanguageCode);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[LanguageService] Successfully converted text');
        return result.translatedText || inputText;
      } else {
        console.warn('[LanguageService] Failed to convert text, returning original');
        return inputText;
      }
    } catch (error) {
      console.error('[LanguageService] Error converting text:', error);
      return inputText;
    }
  }
}

// Export singleton instance
export const languageService = new LanguageService();
export default languageService;
